import * as React from "react";
import { ExternalLink, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
    pageId: number;
    versionId: number;
    versionNum: number;
    r2Prefix: string;
    manifest?: unknown;
}

/**
 * Renders the uploaded landing page (index.html from the bundle) inside a
 * responsive iframe. Uses the authenticated /admin/pages/{id}/versions/{vid}/preview
 * endpoint which streams the bytes from R2 via the Worker.
 */
export function PagePreview({ pageId, versionId, versionNum, r2Prefix, manifest }: Props): React.ReactNode {
    const [viewport, setViewport] = React.useState<"desktop" | "mobile">("desktop");
    const src = `/admin/pages/${pageId}/versions/${versionId}/preview?file=index.html&_t=${versionId}`;

    // Count key manifest facts for a quick glance alongside the preview.
    const stats = React.useMemo(() => extractStats(manifest), [manifest]);

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <CardTitle className="text-base">
                            Preview · v{versionNum}
                        </CardTitle>
                        <CardDescription className="mt-1">
                            Live rendering of <code className="font-mono text-[11px]">{r2Prefix}index.html</code> — same HTML the Worker serves to buyers.
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-1 rounded-md border p-0.5">
                        <Button
                            type="button"
                            size="sm"
                            variant={viewport === "desktop" ? "default" : "ghost"}
                            onClick={() => setViewport("desktop")}
                            data-testid="preview-viewport-desktop"
                            className="h-7 px-2"
                            title="Desktop viewport"
                        >
                            <Monitor className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={viewport === "mobile" ? "default" : "ghost"}
                            onClick={() => setViewport("mobile")}
                            data-testid="preview-viewport-mobile"
                            className="h-7 px-2"
                            title="Mobile viewport (iPhone 12)"
                        >
                            <Smartphone className="h-3.5 w-3.5" />
                        </Button>
                        <Button asChild size="sm" variant="ghost" className="h-7 px-2" title="Open raw HTML">
                            <a href={src} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {stats.length > 0 && (
                    <div className="grid gap-3 rounded-md border bg-muted/30 p-3 text-xs sm:grid-cols-3">
                        {stats.map((s) => (
                            <div key={s.label}>
                                <div className="text-muted-foreground">{s.label}</div>
                                <div className="mt-0.5 font-medium">{s.value}</div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-center rounded-lg border bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(0,0,0,0.03)_10px,rgba(0,0,0,0.03)_11px)] p-3">
                    <div
                        className={
                            viewport === "mobile"
                                ? "w-[390px] rounded-[28px] border-8 border-foreground/80 overflow-hidden shadow-lg"
                                : "w-full rounded-md overflow-hidden border shadow-sm"
                        }
                        style={{ aspectRatio: viewport === "mobile" ? "390 / 844" : "16 / 10" }}
                    >
                        <iframe
                            data-testid="page-preview-iframe"
                            src={src}
                            title={`Preview of page ${pageId} v${versionNum}`}
                            className="h-full w-full bg-white"
                            sandbox="allow-same-origin allow-forms allow-scripts"
                            loading="lazy"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

type ManifestShape = {
    name?: string;
    form?: {
        fields?: Array<{ name?: string; required?: boolean }>;
        mode?: string;
        after_submit?: { mode?: string; url?: string; redirect_url?: string } | null;
    };
    tracking?: {
        ad_sources?: string[];
        click_id_params?: string[];
        conversion_event?: string | null;
    };
};

function extractStats(manifest: unknown): Array<{ label: string; value: string }> {
    if (!manifest || typeof manifest !== "object") return [];
    const m = manifest as ManifestShape;
    const out: Array<{ label: string; value: string }> = [];
    if (m.form?.fields) {
        const required = m.form.fields.filter((f) => f.required).length;
        const total = m.form.fields.length;
        out.push({
            label: "Form fields",
            value: `${total} (${required} required)`,
        });
    }
    if (m.form?.after_submit?.mode) {
        const mode = m.form.after_submit.mode;
        const dest =
            mode === "navigate"
                ? m.form.after_submit.url
                : mode === "redirect"
                    ? m.form.after_submit.redirect_url
                    : null;
        out.push({
            label: "After submit",
            value: dest ? `${mode} → ${dest}` : mode,
        });
    }
    if (m.tracking?.ad_sources) {
        out.push({
            label: "Ad sources",
            value: m.tracking.ad_sources.length > 0 ? m.tracking.ad_sources.join(", ") : "none",
        });
    }
    if (m.tracking?.conversion_event) {
        out.push({
            label: "GA4 conversion",
            value: m.tracking.conversion_event,
        });
    }
    return out;
}
